/**
 * CmsBridge — Production site ↔ CMS communication bridge
 *
 * This component runs ONLY when the website is loaded inside the CMS iframe.
 * It sends postMessage events to the CMS parent window when:
 *   1. A section scrolls into view (IntersectionObserver)
 *   2. The user navigates to a different page (React Router)
 *   3. The language changes
 *
 * Zero impact on normal visitors — the component renders nothing and
 * all observers are cleaned up on unmount.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Maps DOM section IDs / data-attributes to CMS section IDs (from contentMap.ts)
// These are the data-cms-section attributes we add to section root elements.
const SECTION_MAP = {
    // Home page sections (matched via data-cms-section attribute)
    hero: { pageId: "home", sectionId: "hero" },
    featuredVideo: { pageId: "home", sectionId: "featuredVideo" },
    features: { pageId: "home", sectionId: "features" },
    experiences: { pageId: "home", sectionId: "experiences" },
    ownerSection: { pageId: "home", sectionId: "ownerSection" },
    instagram: { pageId: "home", sectionId: "instagram" },
    corner: { pageId: "home", sectionId: "corner" },
    testimonials: { pageId: "home", sectionId: "testimonials" },
    // About page
    aboutHero: { pageId: "about", sectionId: "aboutHero" },
    values: { pageId: "about", sectionId: "values" },
    meetUs: { pageId: "about", sectionId: "meetUs" },
    journey: { pageId: "about", sectionId: "journey" },
    cta: { pageId: "about", sectionId: "cta" },
    // Gallery page
    gallery: { pageId: "gallery", sectionId: "gallery" },
    // Packages page
    packages: { pageId: "packages", sectionId: "packages" },
    packagesIntro: { pageId: "packages", sectionId: "packagesIntro" },
    package7day: { pageId: "packages", sectionId: "package7day" },
    package5day: { pageId: "packages", sectionId: "package5day" },
    package3day: { pageId: "packages", sectionId: "package3day" },
    package1day: { pageId: "packages", sectionId: "package1day" },
    packagesCta: { pageId: "packages", sectionId: "packagesCta" },
    // Contact page — hero + form split
    "contact:hero": { pageId: "contact", sectionId: "contact:hero" },
    contact: { pageId: "contact", sectionId: "contact" },
    // FAQ page — hero + questions split
    "faq:hero": { pageId: "contact", sectionId: "faq:hero" },
    faq: { pageId: "contact", sectionId: "faq" },
    // Booking page — hero + form split
    "booking:hero": { pageId: "booking", sectionId: "booking:hero" },
    booking: { pageId: "booking", sectionId: "booking" },
};

// Maps URL path segments to CMS page IDs
const PAGE_MAP = {
    "": "home",
    about: "about",
    packages: "packages",
    gallery: "gallery",
    contact: "contact",
    faq: "contact",
    book: "booking",
    privacy: "legal",
    terms: "legal",
    cookies: "legal",
    // EN experience detail pages
    "husky-ride": "detailPages",
    "snowmobile-safari": "detailPages",
    "northern-lights": "detailPages",
    accommodation: "detailPages",
    "lapland-holiday-packages": "packages",
    // SV
    hundspann: "detailPages",
    skotersafari: "detailPages",
    norrsken: "detailPages",
    boende: "detailPages",
    paketresor: "packages",
    // DE
    "husky-tour": "detailPages",
    "schneemobil-safari": "detailPages",
    nordlichter: "detailPages",
    "lappland-reisepakete": "packages",
    // PL
    "pakiety-laponii": "packages",
};

const CMS_ORIGIN = "http://localhost:5173"; // Dev CMS
const CMS_ORIGINS = [
    CMS_ORIGIN,
    "https://coldexperience-cms.netlify.app", // Production CMS
];

function isInsideCmsIframe() {
    try {
        return window.parent !== window;
    } catch {
        return false;
    }
}

function sendToCms(type, payload) {
    if (!isInsideCmsIframe()) return;
    const message = { source: "coldexperience-bridge", type, ...payload };
    // Send to all possible CMS origins
    CMS_ORIGINS.forEach((origin) => {
        try {
            window.parent.postMessage(message, origin);
        } catch {
            // Cross-origin — expected if wrong origin, ignore
        }
    });
    // Fallback: send with wildcard (safe because payload is non-sensitive)
    try {
        window.parent.postMessage(message, "*");
    } catch {
        // ignore
    }
}

export default function CmsBridge() {
    const location = useLocation();
    const { i18n } = useTranslation();
    const observerRef = useRef(null);
    const currentSectionRef = useRef(null);

    // ── Only activate inside CMS iframe ──────────────────────────────────
    useEffect(() => {
        if (!isInsideCmsIframe()) return;

        // Signal to CMS that bridge is ready
        sendToCms("bridge-ready", { url: window.location.href });

        // Listen for CMS commands (e.g., "scroll to section X")
        function handleMessage(event) {
            if (!event.data || event.data.source !== "coldexperience-cms") return;

            if (event.data.type === "scroll-to-section") {
                let el = document.querySelector(
                    `[data-cms-section="${event.data.sectionId}"]`
                );
                // Fallback: CMS may send a CMS section ID (e.g. "bookingHero")
                // that doesn't match the DOM attribute (e.g. "booking:hero").
                // Reverse-lookup through SECTION_MAP to find the correct key.
                if (!el) {
                    const fallbackKey = Object.keys(SECTION_MAP).find(
                        (key) => SECTION_MAP[key].sectionId === event.data.sectionId
                    );
                    if (fallbackKey) {
                        el = document.querySelector(
                            `[data-cms-section="${fallbackKey}"]`
                        );
                    }
                }
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }
        }

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // ── Track page navigation ────────────────────────────────────────────
    useEffect(() => {
        if (!isInsideCmsIframe()) return;

        const pathParts = location.pathname.split("/").filter(Boolean);
        // Remove language prefix (e.g., "en", "sv")
        const pagePath = pathParts.length > 1 ? pathParts[pathParts.length - 1] : "";
        const pageId = PAGE_MAP[pagePath] || "home";
        const lang = pathParts[0] || "en";

        sendToCms("navigate", {
            url: location.pathname,
            pageId,
            lang,
        });
    }, [location.pathname]);

    // ── Track language changes ───────────────────────────────────────────
    useEffect(() => {
        if (!isInsideCmsIframe()) return;
        sendToCms("language-change", { lang: i18n.language });
    }, [i18n.language]);

    // ── IntersectionObserver for section scroll tracking ─────────────────
    // We track ALL sections' visibility globally and debounce to prevent
    // rapid flickering when scrolling between section boundaries.
    const visibilityMap = useRef(new Map()); // element → intersectionRatio
    const debounceTimer = useRef(null);

    useEffect(() => {
        if (!isInsideCmsIframe()) return;

        const currentVisibilityMap = visibilityMap.current;

        // Small delay to let React render sections
        const timer = setTimeout(() => {
            const sections = document.querySelectorAll("[data-cms-section]");
            console.log('[CmsBridge] Observer setup: found', sections.length, 'sections:', Array.from(sections).map(s => s.getAttribute('data-cms-section')));
            if (sections.length === 0) return;

            // Reset visibility tracking
            currentVisibilityMap.clear();

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    console.log('[CmsBridge] Observer fired:', entries.length, 'entries, intersecting:', entries.filter(e => e.isIntersecting).map(e => e.target.getAttribute('data-cms-section')));
                    // Update global visibility map for ALL reported entries
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            currentVisibilityMap.set(
                                entry.target,
                                entry.intersectionRatio
                            );
                        } else {
                            currentVisibilityMap.delete(entry.target);
                        }
                    });

                    // Find the most visible section across ALL tracked sections
                    let bestTarget = null;
                    let bestRatio = 0;

                    currentVisibilityMap.forEach((ratio, target) => {
                        if (ratio > bestRatio) {
                            bestRatio = ratio;
                            bestTarget = target;
                        }
                    });

                    if (bestTarget) {
                        const sectionId =
                            bestTarget.getAttribute("data-cms-section");

                        // Only switch if it's a different section, and debounce
                        if (sectionId && sectionId !== currentSectionRef.current) {
                            // Clear any pending switch
                            if (debounceTimer.current) {
                                clearTimeout(debounceTimer.current);
                            }

                            // Debounce: wait 150ms before committing the switch
                            debounceTimer.current = setTimeout(() => {
                                // Re-check that this section is still the best
                                let stillBest = null;
                                let stillBestRatio = 0;
                                currentVisibilityMap.forEach((r, t) => {
                                    if (r > stillBestRatio) {
                                        stillBestRatio = r;
                                        stillBest = t;
                                    }
                                });

                                const confirmedId =
                                    stillBest?.getAttribute("data-cms-section");
                                if (
                                    confirmedId &&
                                    confirmedId !== currentSectionRef.current
                                ) {
                                    currentSectionRef.current = confirmedId;
                                    const mapping = SECTION_MAP[confirmedId];
                                    console.log('[CmsBridge] Sending scroll event:', confirmedId, 'mapping:', mapping);
                                    sendToCms("scroll", {
                                        sectionId: confirmedId,
                                        pageId: mapping?.pageId || null,
                                        cmsSectionId:
                                            mapping?.sectionId || confirmedId,
                                    });
                                }
                            }, 150);
                        }
                    }
                },
                {
                    // Only the center 50% of the viewport counts — sections
                    // must be well into view before they trigger a switch.
                    threshold: [0.05, 0.2, 0.4, 0.6, 0.8],
                    rootMargin: "-25% 0px -25% 0px",
                }
            );

            sections.forEach((section) => {
                observerRef.current.observe(section);
            });
        }, 500);

        return () => {
            clearTimeout(timer);
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            currentVisibilityMap.clear();
        };
    }, [location.pathname]); // Re-observe when page changes

    // This component renders nothing
    return null;
}
